import React, { useMemo } from "react";
import Loading from "../../components/loading";
import { useGetRecruiterDashboard } from "../../services/api/recruiter-api";
import { useGetPaymentHistory } from "../../services/api/subscription-api";
import {
  Card,
  CardTitle,
  EmptyState,
  Header,
  PageContainer,
  SecondaryButton,
  SummaryCard,
  SummaryGrid,
  TableWrap,
} from "./settings.styles";

const getList = (response) => {
  if (!response) return [];
  const payload = response.data ?? response;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload)) return payload;
  return [];
};

const toMoney = (value) => {
  const numberValue = Number.parseFloat(value || 0);
  if (Number.isNaN(numberValue)) return "0.00 TND";
  return `${numberValue.toFixed(2)} TND`;
};

const ITEM_TYPE_LABELS = {
  template: "Template",
  creditbundle: "Credit Bundle",
};

const formatItem = (payment) => {
  const typeLabel = ITEM_TYPE_LABELS[payment.item_type] || payment.item_type;
  if (payment.item_name && typeLabel) return `${typeLabel} · ${payment.item_name}`;
  if (payment.item_name) return payment.item_name;
  if (typeLabel) return typeLabel;
  return "-";
};

export default function SettingsPage({ role = "default" }) {
  const isDefaultRole = role === "default";
  const { data: recruiterDashboard } = useGetRecruiterDashboard();

  const { data: paymentResponse, isLoading: isLoadingPayments } = useGetPaymentHistory();

  const payments = getList(paymentResponse);

  const successfulPayments = useMemo(
    () => payments.filter((payment) => `${payment.status || ""}`.toLowerCase() === "success"),
    [payments]
  );

  const totalPaid = useMemo(
    () => successfulPayments.reduce((sum, payment) => sum + Number.parseFloat(payment.amount || 0), 0),
    [successfulPayments]
  );

  const renderBillingSection = () => {
    const recruiterCredits = recruiterDashboard?.recruiter?.credits;

    return (
      <>
        <Card>
          <CardTitle>Billing Overview</CardTitle>
          <SummaryGrid>
            <SummaryCard>
              <h3>Total Paid</h3>
              <p>{toMoney(totalPaid)}</p>
            </SummaryCard>
            <SummaryCard>
              <h3>Successful Purchases</h3>
              <p>{successfulPayments.length}</p>
            </SummaryCard>
            <SummaryCard>
              <h3>{isDefaultRole ? "Current Balance" : "Current Credits"}</h3>
              <p>{isDefaultRole ? "N/A" : (recruiterCredits ?? 0)}</p>
            </SummaryCard>
          </SummaryGrid>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
            <SecondaryButton onClick={() => window.alert("Stripe portal integration is coming soon.")}>
              Manage Cards
            </SecondaryButton>
          </div>
        </Card>

        <Card>
          <CardTitle>Payment History</CardTitle>
          {isLoadingPayments ? (
            <Loading text="Loading payments" />
          ) : payments.length === 0 ? (
            <EmptyState>No payment records yet.</EmptyState>
          ) : (
            <TableWrap>
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Item</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Promo Code</th>
                    <th>Date</th>
                    <th>Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => {
                    const receiptUrl = payment?.receipt_url || payment?.invoice_url || payment?.payment_data?.receipt_url || payment?.payment_data?.invoice_url;
                    return (
                      <tr key={payment.id}>
                        <td>{payment.id}</td>
                        <td>{formatItem(payment)}</td>
                        <td>{toMoney(payment.amount)}</td>
                        <td>{payment.status || "pending"}</td>
                        <td>
                          {payment.promo_code ? (
                            <span title={`${payment.discount_percent}% off - original price ${toMoney(payment.original_price)}`}>
                              🎟️ {payment.promo_code} (-{payment.discount_percent}%)
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>{payment.date ? new Date(payment.date).toLocaleDateString() : "-"}</td>
                        <td>
                          {receiptUrl ? (
                            <a href={receiptUrl} target="_blank" rel="noreferrer">Download</a>
                          ) : (
                            "Not available"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableWrap>
          )}
        </Card>
      </>
    );
  };

  return (
    <PageContainer>
      <Header>
        <h1>Settings</h1>
        <p>Billing and purchases. Profile information is managed in Edit Profile.</p>
      </Header>

      {renderBillingSection()}
    </PageContainer>
  );
}
